import { QueryClient } from '@tanstack/react-query';

/**
 * Shared QueryClient for the tenant app (#161).
 *
 * WHY THIS EXISTS
 * ---------------
 * Every read in this app was a bare `axios` call inside `useEffect`. With 146
 * components fetching that way there is no request deduplication and no cache,
 * so two components wanting the same data issue two requests, and navigating
 * away and back re-issues all of them. The credits lookup alone
 * (`/user/report-services/{id}`) is performed by 65 components, each on mount.
 *
 * DEFAULTS, AND WHY THEY ARE THESE VALUES
 * ---------------------------------------
 * `staleTime: 30s` — the floor, not the target. Anything genuinely static
 * (masters, dropdowns) overrides it upward at the call site. 30s is chosen so
 * that a single navigation flow — open a page, drill in, come back — serves
 * from cache, while a user idling and returning still gets fresh data.
 *
 * `refetchOnWindowFocus: false` — deliberately off. This is a back-office tool
 * where users keep the tab open beside a phone call; refetching every page's
 * data on every alt-tab would multiply load for no user benefit. Screens that
 * genuinely need liveness opt in.
 *
 * `retry: 1` — one retry covers a dropped connection. Higher counts are
 * dangerous here: several read endpoints in this app are credit-charging (see
 * config/billable_routes.php), so an automatic retry storm spends the tenant's
 * money. Mutations do not retry at all for the same reason.
 *
 * `refetchOnMount: true` (the library default) is KEPT. It does not mean "always
 * refetch" — it means "refetch if stale". Combined with staleTime it delivers
 * the fix the issue asks for while never serving data older than staleTime.
 */
export const createQueryClient = () =>
    new QueryClient({
        defaultOptions: {
            queries: {
                staleTime: 30_000,
                gcTime: 5 * 60_000,
                refetchOnWindowFocus: false,
                retry: 1,
            },
            mutations: {
                // Never automatically re-send a write. In this app writes trigger
                // paid third-party calls; a silent retry is a double charge.
                retry: 0,
            },
        },
    });

/** Cache keys in one place so an invalidation cannot miss a consumer by typo. */
export const queryKeys = {
    // One hierarchy, not two (#446). These used to be 'report-service' and
    // 'report-services' — different first segments, so the "invalidate every
    // cached balance" call could never match a single per-service entry.
    // React Query matches element-wise by prefix, and 'report-services' is not
    // a prefix of ['report-service', '4']; it silently matched nothing.
    //
    // Sharing the first segment is what makes the prefix match work, so the
    // no-arg invalidation now reaches every per-service entry — and a future
    // list query slots in under the same root for free.
    reportServices: () => ['report-services'],
    reportService: (serviceId) => ['report-services', String(serviceId)],
    // BFF page aggregates (#162). Params are part of the key because
    // /page-data/lead-wizard?lead_id=1 and ?lead_id=2 are different pages, and a
    // key that ignored them would serve one lead's data under another's URL.
    pageData: (page, params = {}) => [
        'page-data',
        page,
        Object.fromEntries(
            Object.entries(params)
                .filter(([, v]) => v !== undefined && v !== null && v !== '')
                .sort(([a], [b]) => a.localeCompare(b))
        ),
    ],
};

export default createQueryClient;
